import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IssueManagementService } from './issue-management.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-issue-add',
    templateUrl: './issue-add.component.html',
    styleUrls: ['./issue-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IssueAddComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;
  loadingData = false;

  // Modal data
  modalTitle = 'Create New Issue';
  modalData: any = {};
  taxYears: any[] = [];
  selectedTaxYear: any;
  selectedScenario: any;
  issuesData: any[] = [];
  multiYearDropDownOptions: any[] = [];
  adjTypeOptions: any[] = [];
  timingOptions: any[] = [];

  // Form data
  shortDescription: string = '';
  detailedDescription: string = '';
  selectedStdDesc: any = null;
  primaryCitation: string = '';
  multiYear: boolean = false;
  multiYearDropDown: any = null;
  adjType: any = null;
  filedScheduleFormCheck: boolean = false;
  timing: any = null;
  effectYrs: string = 'No';
  explanation: string = '';

  // Tax year scenarios
  taxYearScenarios: any[] = [];
  selectdTYScenario: any[] = [];
  invalidTaxYearScenarios: boolean = true;

  // Dropdown list
  stdDescList: any[] = [];
  adjustmentTypes: any[] = [];
  selectedType: any = null;

  // Form validation
  formErrors: any = {
    shortDesc: '',
    detailedDesc: '',
    stdDesc: ''
  };
  
  // Validation flags
  isShortDescUnique: boolean = true;
  isDetailedDescUnique: boolean = true;

  // Config data
  GENERAL_CONFIG: any = {};
  globalService: any = {};
  baseUrl: string = '';

  constructor(
    private issueService: IssueManagementService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close all dropdowns when clicking outside
    this.taxYearScenarios.forEach(item => {
      item.dropdownOpen = false;
    });
    this.cdr.detectChanges();
  }

  @Input('base-urls')
  set _base_urls(urls: string) {
    try {
      const urlsObject = safeJsonInput<any>(urls, null, 'issue-add.parse1');
      if (Object.keys(urlsObject).length > 0) {
        this.baseUrl = urlsObject.base_url || '';
      }
    } catch (e) {
      console.log("Base URLs input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const data = safeJsonInput<any>(headerData, null, 'issue-add.parse2');
      if (Object.keys(data).length > 0) {
        this.globalService = data.globalParams || {};
        this.GENERAL_CONFIG = data.generalConfigData || {};
        if (!this.baseUrl && this.GENERAL_CONFIG.base_url) {
          this.baseUrl = this.GENERAL_CONFIG.base_url;
        }
      }
    } catch (e) {
      console.log("Header data input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('modal-data')
  set _modal_data(modalData: string) {
    try {
      const data = safeJsonInput<any>(modalData, null, 'issue-add.parse3');
      if (Object.keys(data).length > 0) {
        this.modalData = data;
        
        // Convert arrays from object to array if needed (AngularJS serialization issue)
        this.taxYears = this.convertToArray(data.taxYears) || [];
        this.selectedTaxYear = data.selectedTaxYear;
        this.selectedScenario = data.selectedScenario;
        this.issuesData = this.convertToArray(data.issuesData) || [];
        this.multiYearDropDownOptions = this.convertToArray(data.multiYearDropDownOptions) || [];
        this.adjTypeOptions = this.convertToArray(data.adjTypeOptions) || [];
        this.timingOptions = this.convertToArray(data.timingOptions) || [];
        this.init();
      }
    } catch (e) {
      console.log("Modal data input not yet stable. Still waiting to get resolved.");
    }
  }

  // Helper function to convert object with numeric keys to array
  convertToArray(obj: any): any[] {
    if (Array.isArray(obj)) {
      return obj;
    }
    if (obj && typeof obj === 'object') {
      // Check if it's an object with numeric keys (serialized array)
      const keys = Object.keys(obj);
      if (keys.length > 0 && keys.every(key => !isNaN(Number(key)))) {
        return Object.values(obj);
      }
    }
    return obj || [];
  }

  ngOnInit() {
    // Component initialization
  }

  onShortDescriptionChange() {
    if (this.shortDescription && this.shortDescription.trim()) {
      this.isShortDescUnique = !this.issuesData.some(
        issue => issue.issue_short_desc === this.shortDescription
      );
    } else {
      this.isShortDescUnique = true;
    }
  }

  onDetailedDescriptionChange() {
    if (this.detailedDescription && this.detailedDescription.trim()) {
      this.isDetailedDescUnique = !this.issuesData.some(
        issue => issue.issue_desc === this.detailedDescription
      );
    } else {
      this.isDetailedDescUnique = true;
    }
  }

  init() {
    try {
      if (this.baseUrl) {
        this.loadAdjustmentTypes();
        this.getTYScenarios();
        this.getStdDescList();
      }
    } catch (e) {
      console.error('Error in issue-add init:', e);
    }
  }

  loadAdjustmentTypes() {
    if (!this.selectedTaxYear || !this.baseUrl) return;
    
    this.issueService.getScenarios(this.baseUrl, this.selectedTaxYear.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.adjustmentTypes = response.scenarioList || [];
          this.selectedType = this.adjustmentTypes.length > 0 ? this.adjustmentTypes[0] : null;
          // Trigger validation when selectedType changes
          this.onSelectedTypeChange();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment types:', error);
      }
    );
  }

  onSelectedTypeChange() {
    // Re-validate descriptions when selectedType changes (like AngularJS $watch)
    if (this.selectedType) {
      this.onShortDescriptionChange();
      this.onDetailedDescriptionChange();
    }
  }

  getTYScenarios() {
    if (!this.baseUrl) return;

    this.issueService.getTYScenarios(this.baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.taxYearScenarios = response.taxyearScenarioList || [];
          this.taxYearScenarios.forEach(item => {
            item.selectedScenario = [];
            item.dropdownOpen = false;
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading tax year scenarios:', error);
      }
    );
  }

  getStdDescList() {
    if (!this.baseUrl) return;

    this.issueService.getStdDesc(this.baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.stdDescList = response.stdDescList || [];
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading standard descriptions:', error);
      }
    );
  }

  onScenarioSelectionChanged() {
    this.selectdTYScenario = [];
    this.taxYearScenarios.forEach(item => {
      if (item.selectedScenario && item.selectedScenario.length > 0) {
        this.selectdTYScenario.push(item.selectedScenario);
      }
    });

    this.invalidTaxYearScenarios = this.selectdTYScenario.length === 0;
  }

  toggleDropdown(index: number, event: Event) {
    event.stopPropagation();
    // Close all other dropdowns
    this.taxYearScenarios.forEach((item, idx) => {
      if (idx !== index) {
        item.dropdownOpen = false;
      }
    });
    // Toggle current dropdown
    this.taxYearScenarios[index].dropdownOpen = !this.taxYearScenarios[index].dropdownOpen;
    this.cdr.detectChanges();
  }

  getSelectedText(item: any): string {
    if (!item.selectedScenario || item.selectedScenario.length === 0) {
      return 'Select scenario';
    }
    if (item.selectedScenario.length === 1) {
      return item.selectedScenario[0].scenario_desc;
    }
    return `${item.selectedScenario.length} selected`;
  }

  isSelected(item: any, scenario: any): boolean {
    return item.selectedScenario && item.selectedScenario.some((s: any) => s.scenario === scenario.scenario);
  }

  toggleSelection(item: any, scenario: any) {
    if (!item.selectedScenario) {
      item.selectedScenario = [];
    }
    
    const index = item.selectedScenario.findIndex((s: any) => s.scenario === scenario.scenario);
    if (index > -1) {
      item.selectedScenario.splice(index, 1);
    } else {
      item.selectedScenario.push(scenario);
    }
    
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  selectAll(item: any) {
    item.selectedScenario = [...item.scenarioList];
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  unselectAll(item: any) {
    item.selectedScenario = [];
    this.onScenarioSelectionChanged();
    this.cdr.detectChanges();
  }

  toggleMultiYear() {
    if (this.multiYear && this.multiYearDropDownOptions.length > 0) {
      this.multiYearDropDown = this.multiYearDropDownOptions[0];
    } else {
      this.multiYearDropDown = null;
    }
  }

  isFormValid(): boolean {
    return !!(
      this.shortDescription &&
      this.shortDescription.trim().length > 0 &&
      this.shortDescription.length <= 35 &&
      this.isShortDescUnique &&
      this.detailedDescription &&
      this.detailedDescription.trim().length > 0 &&
      this.isDetailedDescUnique &&
      this.selectedStdDesc &&
      !this.invalidTaxYearScenarios
    );
  }

  save() {
    if (!this.isFormValid()) {
      this._snackBar.open('Please fill all required fields', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build tax year object
    const txYrObj: any = {};
    this.selectdTYScenario.forEach(scenarioGroup => {
      scenarioGroup.forEach((scenario: any) => {
        txYrObj[scenario.tax_year] = txYrObj[scenario.tax_year] || [];
        txYrObj[scenario.tax_year].push(scenario.scenario);
      });
    });

    // Build parameters string
    let codeSeqMapStr = '';
    for (const year in txYrObj) {
      codeSeqMapStr += `${year}=${txYrObj[year].toString()}&`;
    }
    codeSeqMapStr += `issue_desc=${encodeURIComponent(this.detailedDescription)}&`;
    codeSeqMapStr += `issue_short_desc=${encodeURIComponent(this.shortDescription)}&`;
    codeSeqMapStr += `std_desc_id=${this.selectedStdDesc.std_desc_id}&`;
    codeSeqMapStr += `primary_citation=${this.primaryCitation ? this.primaryCitation : ''}&`;
    codeSeqMapStr += `multi_yr=${this.multiYear && this.multiYearDropDown ? this.multiYearDropDown.value : 'NA'}&`;
    codeSeqMapStr += `multi_yr_check=${this.multiYear}&`;
    codeSeqMapStr += `adj_type=${this.adjType ? this.adjType.CODE_DESC : ''}&`;
    codeSeqMapStr += `filed_schedule_form_check=${this.filedScheduleFormCheck}&`;
    codeSeqMapStr += `timing=${this.timing ? this.timing.value : ''}&`;
    codeSeqMapStr += `effect_other_yrs=${this.effectYrs}&`;
    codeSeqMapStr += `explanation=${this.explanation ? this.explanation : ''}`;

    this.issueService.saveIssue(this.baseUrl, codeSeqMapStr).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "0") {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('New Issue Created Successfully!', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving issue:', error);
        this._snackBar.open('Something went wrong while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.shortDescription = '';
    this.detailedDescription = '';
    this.selectedStdDesc = null;
    this.primaryCitation = '';
    this.multiYear = false;
    this.multiYearDropDown = null;
    this.adjType = null;
    this.filedScheduleFormCheck = false;
    this.timing = null;
    this.effectYrs = 'No';
    this.explanation = '';
    this.taxYearScenarios.forEach(item => {
      item.selectedScenario = [];
    });
    this.selectdTYScenario = [];
    this.invalidTaxYearScenarios = true;
    
    // Reset validation flags
    this.isShortDescUnique = true;
    this.isDetailedDescUnique = true;
    
    // Trigger change detection
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}

